import React, { useState, useEffect, useRef } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { colors } from '../../../theme/palette'
import { ChevronDownFill, ChevronRight, Close } from '../../../assets/icons'
import { Input } from '../FormElememts/Input'
import { Typography } from '../Typography/Typography'
import { filterByValue } from '../../../utils/searchHelper'
import { Case } from '../../../models/Case'
import { formatDate } from '../../../utils/format'
import { Button } from '../Button/Button'
import { L } from '../../providers/Lokalise'

type RuleNames =
    | 'container'
    | 'input'
    | 'suggestions'
    | 'suggestionItem'
    | 'searchButton'
    | 'button'
    | 'suggestionsList'

interface StyleProps {
    query: string
    isOpen: boolean
}

type Props = {
    label: string
    required: boolean
    suggestions: string[]
    onSelect: (value: string) => void
    onChange?: (value: string) => void
    style?: React.CSSProperties
    autoFocus?: boolean
    cases?: Case[]
    defaultSuggestion?: string
    hasError?: boolean
}

const useStyles = createUseStyles<RuleNames, StyleProps, Jss.Theme>({
    container: {
        position: 'relative',
        width: '100%',
    },
    input: {
        width: '100%',
    },
    suggestions: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: colors.white,
        border: `1px solid ${colors.borderColor}`,
        borderTop: 'none',
        borderRadius: '0 0 6px 6px',
        maxHeight: '300px',
        overflowY: 'auto',
        zIndex: 1000,
        display: ({ isOpen }) => (isOpen ? 'block' : 'none'),
    },
    suggestionItem: {
        padding: '8px 16px',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: colors.gray500,
        },
    },
    searchButton: {
        position: 'absolute',
        right: '10px',
        top: '50%',
        transform: ({ isOpen }) =>
            isOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'transform 0.2s ease',
    },
    button: {
        position: 'absolute',
        right: '40px',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    suggestionsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
})

const Autocomplete: React.FC<Props> = ({
    label,
    required,
    suggestions,
    onSelect,
    onChange,
    autoFocus,
    cases,
    defaultSuggestion,
    hasError,
    ...props
}) => {
    const [query, setQuery] = useState<string | null>(null)
    const [isOpen, setIsOpen] = useState(false)
    const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([])
    const [filteredItems, setFilteredItems] = useState<Case[]>([])
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const containerRef = useRef<HTMLDivElement>(null)
    const theme = useTheme<Jss.Theme>()
    const classes = useStyles({ query, isOpen, theme })

    useEffect(() => {
        if (defaultSuggestion) {
            const found = cases?.find(
                (item) => String(item.caseId) === String(defaultSuggestion)
            )
            if (found) {
                setQuery(`${found.newlyHired.name}, ${found.caseNumber}`)
            } else {
                setQuery(defaultSuggestion)
            }
        }
    }, [defaultSuggestion])

    // Initialize filteredItems with cases when cases are first provided
    useEffect(() => {
        if (cases && cases.length > 0) {
            setFilteredItems(cases)
        }
    }, [cases])

    useEffect(() => {
        if (query !== null) {
            const filtered = suggestions.filter((suggestion) =>
                suggestion.toLowerCase().includes(query.toLowerCase())
            )

            if (cases) {
                let items = filterByValue([...cases], query, [
                    'name',
                    'caseNumber',
                ])

                if (items.length === 0) {
                    items = cases
                }
                setFilteredItems(items)
            }

            setFilteredSuggestions(filtered)
        }
    }, [query, suggestions])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent): void => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ): void => {
        const { value } = e.target
        setQuery(value)
        setHighlightedIndex(-1)
        setIsOpen(true) // Open dropdown when user starts typing

        if (onChange) {
            onChange(value)
        }
    }

    const handleToggleDropdown = (): void => {
        setIsOpen(!isOpen)
    }

    const handleSuggestionClick = (suggestion: Case): void => {
        setQuery(`${suggestion.newlyHired.name}, ${suggestion.caseNumber}`)
        setIsOpen(false)
        onSelect(suggestion.caseId)
    }

    const handleClear = (): void => {
        setQuery('')
        setIsOpen(false)
        if (onChange) {
            onChange('')
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent): void => {
        const items = query ? filteredItems : cases || []

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setHighlightedIndex((prevIndex) =>
                    prevIndex < items.length - 1 ? prevIndex + 1 : prevIndex
                )
                break
            case 'ArrowUp':
                e.preventDefault()
                setHighlightedIndex((prevIndex) =>
                    prevIndex > 0 ? prevIndex - 1 : prevIndex
                )
                break
            case 'Enter':
                e.preventDefault()
                if (highlightedIndex >= 0 && items[highlightedIndex]) {
                    handleSuggestionClick(items[highlightedIndex])
                }
                break
            case 'Escape':
                e.preventDefault()
                setIsOpen(false)
                setHighlightedIndex(-1)
                break
            default:
                break
        }
    }

    return (
        <div className={classes.container} ref={containerRef} {...props}>
            <Input
                type="text"
                id={label}
                value={query}
                required={required}
                label={label}
                autoFocus={autoFocus}
                autoComplete="off"
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsOpen(true)}
                hasError={hasError}
            />
            {query && (
                <button
                    type="button"
                    className={classes.button}
                    onClick={handleClear}
                >
                    <Close width="14px" color={colors.green500} />
                </button>
            )}
            <button
                className={classes.searchButton}
                type="button"
                onClick={handleToggleDropdown}
            >
                <div
                    style={{
                        transform: isOpen ? 'rotate(270deg)' : 'rotate(-90deg)',
                    }}
                >
                    <ChevronRight width="8px" color={colors.green500} />
                </div>
            </button>
            <div className={classes.suggestions}>
                <div className={classes.suggestionsList}>
                    {(query ? filteredItems : cases || []).map(
                        (suggestion, index) => (
                            <Button
                                key={`suggestion-${suggestion.caseId}`}
                                type="action"
                                onClick={() =>
                                    handleSuggestionClick(suggestion)
                                }
                                style={{
                                    backgroundColor:
                                        index === highlightedIndex
                                            ? colors.gray500
                                            : 'transparent',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.3rem',
                                        padding: '.5rem',
                                        borderBottom:
                                            index !==
                                            (query
                                                ? filteredItems
                                                : cases || []
                                            ).length -
                                                1
                                                ? `1px solid ${colors.borderColor}`
                                                : 'none',
                                    }}
                                >
                                    <Typography
                                        size="XXS"
                                        color={colors.gray900}
                                    >
                                        <span
                                            style={{
                                                marginRight: '2rem',
                                            }}
                                        >
                                            {suggestion.caseNumber}
                                        </span>
                                        <span>
                                            {`${L(
                                                'LØN_SAMMENLÆG_SAG_AUTOCOMPLETE_MODTAGET_DATO'
                                            )} ${formatDate(
                                                new Date(
                                                    suggestion.receivedDate
                                                )
                                            )}`}
                                        </span>
                                    </Typography>
                                    <Typography size="XS">
                                        {suggestion.newlyHired?.name}
                                    </Typography>
                                    <Typography size="XXS">
                                        {suggestion.newlyHired?.workplace?.navn}{' '}
                                        -{' '}
                                        {suggestion.newlyHired?.position?.navn}
                                    </Typography>
                                </div>
                            </Button>
                        )
                    )}
                </div>
            </div>
        </div>
    )
}

Autocomplete.defaultProps = {
    onChange: undefined,
    style: undefined,
    autoFocus: false,
}

export default Autocomplete
